import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { RouterOutlet } from '@angular/router';
import { switchMap, tap } from 'rxjs';
import { ThemeBaseComponent } from 'src/app/_core';
import { ProductRepository } from 'src/app/_core/repositories/product.repository';
import { TicketService } from 'src/app/_core/services/ticket.service';
import { EcommerceProductVariant } from 'src/app/_core/types/types';
import { FooterComponent } from 'src/app/_shared/components/footer/footer.component';
import { NavbarComponent } from 'src/app/_shared/components/navbar/navbar.component';
import { CartRepository } from '../modules/cart/state/cart.repository';

@Component({
  standalone: true,
  selector: 'neopos-public-layout',
  templateUrl: './public.layout.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterOutlet, FooterComponent, NavbarComponent],
})
export class PublicLayoutComponent extends ThemeBaseComponent {
  private readonly _ticketService = inject(TicketService);
  private readonly _cartRepository = inject(CartRepository);
  private readonly _productRepository = inject(ProductRepository);

  queryPriceList = toSignal(
    toObservable(this.priceListId).pipe(
      switchMap((priceListId) => {
        return this._ticketService.queryPriceList(priceListId!).result$.pipe(
          tap(({ data }) => {
            if (data) {
              const productVariants: EcommerceProductVariant[] =
                data.productVariants
                  .filter((variant) => {
                    return (
                      !!variant.productVariant &&
                      (variant.productVariant.product?.isBlocked === false ||
                        (variant.productVariant.sets?.[0]?.status === true &&
                          variant.productVariant.sets?.[0]?.products?.every(
                            (product) => product.isBlocked === false
                          )))
                    );
                  })
                  .map((variant) => {
                    this._cartRepository.variantCategoryMap.set(
                      variant.productVariant.id,
                      variant.ecommerceCategory?.id ?? ''
                    );
                    return {
                      ...variant.productVariant,
                      ecommerceCategory: variant.ecommerceCategory!,
                      ecommerceType: variant.ecommerceType!,
                      ecommercePriceListPrice:
                        data.productVariants.find(
                          (pa) =>
                            pa.productVariantId === variant.productVariant.id
                        )?.ecommercePriceListPrice || 0,
                      isStartingPrice: variant.isStartingPrice,
                      lowestPrice:
                        variant.lowestPrice ??
                        variant.ecommercePriceListPrice ??
                        0,
                      lastPriceUpdateDate: data.priceAdjustments.find(
                        (pa) =>
                          pa.productVariantId === variant.productVariant.id
                      )?.lastPriceUpdateDate,
                    };
                  });

              this._cartRepository.products.set(productVariants);
              this._cartRepository.filteredProducts.set(
                this._cartRepository.products()
              );

              const ecommerceCategoriesMap = new Map(
                data.ecommerceCategories.map((c) => [c.id, c])
              );

              const categoriesToUpdate =
                this.categoryImages()?.map((ci) => {
                  const category = ecommerceCategoriesMap.get(ci.categoryId);

                  const icon = category?.ecommerceCategoryFiles?.find(
                    (file) => file.file.fileType === 'svg'
                  );

                  return {
                    ...ci,
                    icon: icon
                      ? 'data:image/svg+xml;base64,' + icon?.file?.fileBase
                      : '',
                    name: category?.name,
                  };
                }) ?? [];
              this.setCategories(categoriesToUpdate);

              this._productRepository.setCategories(
                data.ecommerceCategories.map((category) => {
                  return {
                    ...category,
                    ecommerceTypes: category.ecommerceTypes.filter((type) =>
                      data.priceAdjustments.some(
                        (pa) => pa.ecommerceTypeId === type.id
                      )
                    ),
                  };
                })
              );

              const prices: number[] = [
                ...data.priceAdjustments,
                ...(data as any).salesGroups.flatMap(
                  (sg: any) => sg.priceAdjustments
                ),
              ].map((pa) => pa.price);

              this._productRepository.setDefaultFilters({
                categories: [],
                minPrice: Math.min(...prices),
                maxPrice: Math.max(...prices),
              });
            }
          })
        );
      })
    )
  );
}
